import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, BookOpen, CalendarCheck, Chat, Star, Books, Warning, BookmarkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

// Cool-newsprint paper + inky black + poppy red + saffron — editorial bookshop vibe
// Deliberately different from cream/sage/beige palettes used elsewhere.
const PAGE = "#E7EAEC";      // cool bone / pale bluish-grey
const PAGE_LIGHT = "#F2F4F6";
const INK = "#141418";
const INK_SOFT = "#4A4E58";
const POPPY = "#C73E2A";     // bright editorial red
const POPPY_DEEP = "#8F2B1C";
const SAFFRON = "#E5A93B";   // warm amber accent (honey, picks up the Bee Sting cover)
const FOREST = "#205147";    // deep reading-green — used only for 'going' state
const RULE = "#CBD0D6";

const COVERS = "/case-studies/bookclub";

const MEMBERS = [
  { id: "m1", name: "Priya", chapter: 12, color: POPPY },
  { id: "m2", name: "Jim", chapter: 9, color: FOREST },
  { id: "m3", name: "Hannah", chapter: 14, color: SAFFRON },
  { id: "me", name: "You", chapter: 7, color: "#3A5F8A" },
  { id: "m5", name: "Anita", chapter: 11, color: "#8E3B4A" },
  { id: "m6", name: "Lola", chapter: 6, color: "#4E6880" },
];

const BOOK = {
  title: "The Bee Sting",
  author: "Paul Murray",
  year: 2023,
  chapters: 16,
  pages: 656,
  cover: `${COVERS}/bee-sting.jpg`,
  blurb: "The Barnes family is in trouble. Dickie's car business is going under; his wife Imelda is selling off her jewellery on eBay; their clever teenage daughter is doing spectacularly badly at school. What has caused the family's seemingly unstoppable fall from grace?",
};

const CHAPTER_THREADS = [
  { ch: 6, author: "m6", count: 3, last: "…mum's jewellery mention gutted me", spoiler: false },
  { ch: 9, author: "m2", count: 7, last: "Cass at the party — was that deliberate?", spoiler: true },
  { ch: 11, author: "m5", count: 4, last: "The bee sting itself — metaphor watch", spoiler: true },
  { ch: 14, author: "m3", count: 2, last: "PJ, finally. Heartbreaking.", spoiler: true },
];

const MEETING = {
  date: "Sun 28 Apr · 7:00pm",
  host: "Priya",
  venue: "Priya's — 42 Linden Gardens",
  rsvps: { m1: "going", m2: "going", m3: "going", me: "maybe", m5: "going", m6: "declined" },
  preamble: "Dickie's perspective · chapters 1–8 (only). Priya's making chilli.",
};

const SHELF = [
  { title: "Demon Copperhead", author: "Barbara Kingsolver", stars: 4.6, note: "Best read of last year", cover: `${COVERS}/demon-copperhead.jpg` },
  { title: "Birnam Wood", author: "Eleanor Catton", stars: 3.8, note: "Divisive — Jim hated, Hannah loved", cover: `${COVERS}/birnam-wood.jpg` },
  { title: "The Marriage Portrait", author: "Maggie O'Farrell", stars: 4.2, note: null, cover: `${COVERS}/marriage-portrait.jpg` },
  { title: "Trust", author: "Hernan Diaz", stars: 3.5, note: "Clever, a bit cold", cover: `${COVERS}/trust.jpg` },
];

const LENDING = { status: "on-loan", with: "Lola", since: "11 days" };

const TABS = [
  { key: "read", label: "This month", icon: BookOpen },
  { key: "meeting", label: "Meeting", icon: CalendarCheck },
  { key: "threads", label: "Threads", icon: Chat },
  { key: "shelf", label: "Bookshelf", icon: Books },
];

export default function ChapterOne() {
  const [tab, setTab] = useState("read");
  const [members, setMembers] = useState(MEMBERS);
  const [rsvps, setRsvps] = useState(MEETING.rsvps);
  const [revealed, setRevealed] = useState([]);
  const [lending, setLending] = useState(LENDING);
  const navigate = useNavigate();

  const member = (id) => members.find((m) => m.id === id);

  const bumpChapter = (delta) => {
    setMembers((ms) => ms.map((m) => {
      if (m.id !== "me") return m;
      const ch = Math.max(0, Math.min(BOOK.chapters, m.chapter + delta));
      return { ...m, chapter: ch };
    }));
    if (delta > 0) toast.success("Chapter progress updated");
  };

  const rsvp = (status) => {
    setRsvps((r) => ({ ...r, me: status }));
    toast.success(`Marked ${status}`);
  };

  const reveal = (ch) => setRevealed((r) => (r.includes(ch) ? r : [...r, ch]));

  const borrowBook = () => {
    setLending({ status: "on-loan", with: "You", since: "just now" });
    toast.success("Logged — enjoy");
  };
  const returnBook = () => {
    setLending({ status: "shelved", with: null, since: null });
    toast.success("Returned to the club shelf");
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: PAGE, color: INK, fontFamily: "'Manrope', sans-serif" }} data-testid="chapter-one-interactive">
      <header className="border-b" style={{ backgroundColor: PAGE_LIGHT, borderColor: RULE }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/demos/chapter-one" className="opacity-50" style={{ color: INK }}><ArrowLeft size={18} /></Link>
            <div className="h-9 w-9 flex items-center justify-center" style={{ backgroundColor: POPPY }}>
              <BookmarkSimple size={16} weight="fill" color={PAGE_LIGHT} />
            </div>
            <div>
              <div className="text-base font-bold leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>The Linden Gardens Book Club <span className="font-mono text-[10px] opacity-60 font-normal">· Concept mock</span></div>
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-60 mt-0.5">6 members · meets monthly</div>
            </div>
          </div>
          <button onClick={() => navigate("/consultation?demo=chapter-one")} className="text-xs font-semibold px-3 py-2 border" style={{ borderColor: POPPY, color: POPPY }} data-testid="chapter-book-cta">Talk to us about this</button>
        </div>
        <nav className="max-w-5xl mx-auto px-6 flex gap-6 border-t overflow-x-auto" style={{ borderColor: RULE }}>
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} className="relative py-3 text-sm font-medium flex items-center gap-1.5 whitespace-nowrap" style={{ color: tab === t.key ? INK : INK_SOFT }} data-testid={`chapter-tab-${t.key}`}>
                <Icon size={14} weight="fill" /> {t.label}
                {tab === t.key && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: POPPY }} />}
              </button>
            );
          })}
        </nav>
      </header>

      <section className="max-w-5xl mx-auto px-6 py-10">
        {tab === "read" && <ReadTab members={members} member={member} bumpChapter={bumpChapter} lending={lending} borrowBook={borrowBook} returnBook={returnBook} />}
        {tab === "meeting" && <MeetingTab members={members} rsvps={rsvps} rsvp={rsvp} member={member} />}
        {tab === "threads" && <ThreadsTab member={member} revealed={revealed} reveal={reveal} />}
        {tab === "shelf" && <ShelfTab />}
      </section>

      <footer className="border-t mt-8 py-10" style={{ backgroundColor: PAGE_LIGHT, borderColor: RULE }}>
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-3">Concept · a home for books, not a feed</p>
          <Link to="/consultation?demo=chapter-one" className="inline-flex items-center gap-2 text-white px-5 py-3 text-sm font-semibold" style={{ backgroundColor: POPPY }}>Book a 30-min consultation <ArrowRight size={14} weight="bold" /></Link>
        </div>
      </footer>
    </div>
  );
}

function ReadTab({ members, member, bumpChapter, lending, borrowBook, returnBook }) {
  const me = member("me");
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
      {/* Book cover column */}
      <div className="lg:col-span-2">
        <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-2">April read</p>
        <div className="relative aspect-[2/3] mb-5 overflow-hidden shadow-[8px_8px_0_0_rgba(20,20,24,0.18)] bg-black" data-testid="chapter-cover">
          <img src={BOOK.cover} alt={`${BOOK.title} — ${BOOK.author}`} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="border p-4 bg-white" style={{ borderColor: RULE }} data-testid="chapter-lending">
          <p className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-1">Club spare paperback</p>
          {lending.status === "on-loan" ? (
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>With {lending.with}</div>
                <div className="text-xs opacity-70">Since {lending.since}</div>
              </div>
              {lending.with === "You" ? (
                <button onClick={returnBook} className="text-xs font-semibold px-3 py-1.5 border" style={{ borderColor: POPPY, color: POPPY }} data-testid="chapter-return-book">Return to shelf</button>
              ) : (
                <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">On loan</span>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="text-sm opacity-70">In the shed — free to borrow</div>
              <button onClick={borrowBook} className="text-xs font-semibold text-white px-3 py-1.5" style={{ backgroundColor: POPPY }} data-testid="chapter-borrow-book">Borrow</button>
            </div>
          )}
        </div>
      </div>

      {/* Right — blurb + progress */}
      <div className="lg:col-span-3">
        <h1 className="text-5xl font-black leading-[0.95] tracking-tighter mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{BOOK.title}</h1>
        <p className="italic text-lg opacity-70 mb-5" style={{ fontFamily: "'Outfit', serif" }}>— {BOOK.author}</p>
        <p className="text-sm leading-relaxed opacity-80 mb-8">{BOOK.blurb}</p>

        <div className="border p-5 bg-white" style={{ borderColor: RULE }}>
          <div className="flex items-baseline justify-between mb-4">
            <h3 className="font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>Where's everyone at?</h3>
            <span className="text-[10px] font-mono uppercase tracking-widest opacity-60">Chapter · of {BOOK.chapters}</span>
          </div>
          <ul className="space-y-3" data-testid="chapter-progress">
            {members.map((m) => {
              const pct = (m.chapter / BOOK.chapters) * 100;
              return (
                <li key={m.id} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-[11px] flex-shrink-0" style={{ backgroundColor: m.color }}>{m.name[0]}</div>
                  <div className="flex-1">
                    <div className="flex items-baseline justify-between">
                      <span className="font-semibold text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>{m.name}</span>
                      <span className="text-xs font-mono">{m.chapter}</span>
                    </div>
                    <div className="h-1.5 mt-1 rounded-full overflow-hidden" style={{ backgroundColor: "#DDE1E6" }}>
                      <div className="h-full rounded-full" style={{ width: pct + "%", backgroundColor: m.id === "me" ? POPPY : INK_SOFT }} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {/* My progress actions */}
          <div className="mt-5 pt-4 border-t flex items-center justify-between" style={{ borderColor: RULE }}>
            <div>
              <p className="text-[10px] font-mono uppercase tracking-widest opacity-60">You're on chapter</p>
              <div className="text-2xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>{me.chapter}</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => bumpChapter(-1)} disabled={me.chapter <= 0} className="text-xs font-semibold px-3 py-2 border disabled:opacity-30" style={{ borderColor: POPPY, color: POPPY }} data-testid="chapter-bump-minus">– 1</button>
              <button onClick={() => bumpChapter(1)} disabled={me.chapter >= BOOK.chapters} className="text-xs font-semibold px-3 py-2 text-white disabled:opacity-30" style={{ backgroundColor: POPPY }} data-testid="chapter-bump-plus">+ 1 chapter</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MeetingTab({ members, rsvps, rsvp, member }) {
  const going = members.filter((m) => rsvps[m.id] === "going");
  const maybe = members.filter((m) => rsvps[m.id] === "maybe");
  const declined = members.filter((m) => rsvps[m.id] === "declined");
  const myStatus = rsvps.me;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
      <div className="lg:col-span-2">
        <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-2">Next meeting</p>
        <h1 className="text-5xl font-black tracking-tighter leading-[0.95] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{MEETING.date}</h1>
        <div className="mb-6">
          <p className="text-lg italic opacity-80" style={{ fontFamily: "'Outfit', serif" }}>{MEETING.venue}</p>
          <p className="text-sm opacity-60 mt-1">Hosted by {MEETING.host}</p>
        </div>
        <div className="border-l-4 pl-5 py-2 mb-8" style={{ borderColor: POPPY }}>
          <p className="text-sm italic leading-relaxed" style={{ fontFamily: "'Outfit', serif" }}>"{MEETING.preamble}"</p>
        </div>

        <div className="border p-5 bg-white" style={{ borderColor: RULE }} data-testid="chapter-rsvp">
          <p className="text-[10px] font-mono uppercase tracking-widest opacity-60 mb-3">Your RSVP</p>
          <div className="flex gap-2">
            {[
              { k: "going", label: "Going", c: FOREST },
              { k: "maybe", label: "Maybe", c: SAFFRON },
              { k: "declined", label: "Can't make it", c: INK_SOFT },
            ].map((o) => (
              <button key={o.k} onClick={() => rsvp(o.k)} className="flex-1 py-3 text-sm font-semibold border-2" style={{ borderColor: myStatus === o.k ? o.c : RULE, backgroundColor: myStatus === o.k ? o.c : "white", color: myStatus === o.k ? "white" : INK }} data-testid={`chapter-rsvp-${o.k}`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div>
        <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-4">Attendance · {going.length} going</p>
        <RSVPGroup title="Going" colour={FOREST} members={going} member={member} />
        <RSVPGroup title="Maybe" colour={SAFFRON} members={maybe} member={member} />
        <RSVPGroup title="Declined" colour={INK_SOFT} members={declined} member={member} />
      </div>
    </div>
  );
}

function RSVPGroup({ title, colour, members, member: memberLookup }) {
  if (!members.length) return null;
  return (
    <div className="mb-5">
      <p className="text-[10px] font-mono uppercase tracking-widest font-bold mb-2" style={{ color: colour }}>{title}</p>
      <ul className="space-y-1.5">
        {members.map((m) => (
          <li key={m.id} className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-white font-bold text-[10px]" style={{ backgroundColor: m.color }}>{m.name[0]}</div>
            <span className="text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>{m.name}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ThreadsTab({ member, revealed, reveal }) {
  return (
    <div>
      <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">Spoiler-safe</p>
      <h1 className="text-5xl font-black tracking-tighter leading-[0.95] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>Chapter threads</h1>
      <p className="text-sm opacity-70 mb-8 max-w-xl italic" style={{ fontFamily: "'Outfit', serif" }}>"Threads beyond the chapter you've reached are hidden. Tap to reveal — you were warned."</p>

      <ul className="space-y-3" data-testid="chapter-threads">
        {CHAPTER_THREADS.map((t) => {
          const author = member(t.author);
          const isRevealed = !t.spoiler || revealed.includes(t.ch);
          return (
            <li key={t.ch} className="bg-white border-2 p-4 flex items-start gap-4" style={{ borderColor: RULE }} data-testid={`chapter-thread-${t.ch}`}>
              <div className="w-12 h-12 flex flex-col items-center justify-center flex-shrink-0" style={{ backgroundColor: POPPY, color: PAGE_LIGHT }}>
                <span className="text-[8px] font-mono uppercase tracking-widest">CH</span>
                <span className="text-lg font-black leading-none" style={{ fontFamily: "'Outfit', sans-serif" }}>{t.ch}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-white font-bold text-[9px]" style={{ backgroundColor: author.color }}>{author.name[0]}</div>
                  <span className="text-xs font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>{author.name}</span>
                  <span className="text-[10px] font-mono opacity-60 uppercase tracking-widest">· {t.count} replies</span>
                </div>
                {isRevealed ? (
                  <p className="text-sm italic" style={{ fontFamily: "'Outfit', serif" }}>"{t.last}"</p>
                ) : (
                  <button onClick={() => reveal(t.ch)} className="text-sm flex items-center gap-1.5 opacity-70 hover:opacity-100" data-testid={`chapter-reveal-${t.ch}`}>
                    <Warning size={12} weight="fill" style={{ color: SAFFRON }} /> <span className="italic">Spoilers past your current chapter · tap to reveal</span>
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ShelfTab() {
  return (
    <div>
      <p className="text-xs font-mono uppercase tracking-widest opacity-60 mb-1">Everything we've read</p>
      <h1 className="text-5xl font-black tracking-tighter leading-[0.95] mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>The bookshelf</h1>

      {/* Shelf row — books lean on a warm wood rail */}
      <div className="relative mb-10">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-5" data-testid="chapter-shelf-row">
          {SHELF.map((b) => (
            <div key={b.title} className="flex flex-col">
              <div className="aspect-[2/3] overflow-hidden bg-black shadow-[6px_6px_0_0_rgba(20,20,24,0.15)] transition-transform hover:-translate-y-1" data-testid={`chapter-shelf-cover-${b.title.replace(/\s+/g, '-').toLowerCase()}`}>
                <img src={b.cover} alt={`${b.title} — ${b.author}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          ))}
        </div>
        {/* wooden shelf bar */}
        <div className="h-2 mt-1" style={{ background: `linear-gradient(180deg, ${INK} 0%, ${INK_SOFT} 100%)` }} />
        <div className="h-1" style={{ backgroundColor: INK_SOFT, opacity: 0.35 }} />
      </div>

      <ul className="space-y-3" data-testid="chapter-shelf">
        {SHELF.map((b) => (
          <li key={b.title} className="bg-white border-2 p-5 flex items-center gap-5" style={{ borderColor: RULE }}>
            <div className="w-16 h-24 flex-shrink-0 overflow-hidden bg-black">
              <img src={b.cover} alt={b.title} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>{b.title}</h3>
              <p className="text-sm italic opacity-70" style={{ fontFamily: "'Outfit', serif" }}>— {b.author}</p>
              {b.note && <p className="text-xs italic opacity-60 mt-1">"{b.note}"</p>}
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 justify-end">
                <Star size={14} weight="fill" style={{ color: SAFFRON }} />
                <span className="font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>{b.stars}</span>
              </div>
              <div className="text-[9px] font-mono uppercase tracking-widest opacity-60">Club avg</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
